import { Card, Text } from '@/components/DesignSystem';
import { QueryKeys } from '@/constants/QueryKeys';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { ChartPosition } from '@/utilities/ChurnCustomer';
import { ChurnStatus } from "@shared/enums";
import { formatNumber, formatPercent } from '@shared/utilities';
import { PieDataItemOption } from 'echarts/types/src/chart/pie/PieSeries';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { ChartOptions, TierProgressPieChart } from './InsightPieChart';

interface InsightChurnSectionProps {
  retailerId: string,
}

interface PieDataItem extends PieDataItemOption {
  labelText: string,
}

export const InsightChurnPieChartSection = ({
  retailerId,
}: InsightChurnSectionProps) => {
  const { t } = useTranslation();

  const { data: churnStatusCounts } = useQuery(
    [QueryKeys.GET_CHURN_CUSTOMER_COUNT, retailerId],
    () => RetailerApi.getCustomerChurnPerStatus(retailerId),
  );

  const totalCount = Object.values(churnStatusCounts ?? {}).reduce((sum, count) => sum + count, 0);

  const chartOptions: ChartOptions = {
    chartLegend: {
      borderRadius: 1,
      bottom: "0%",
      itemGap: 12,
      itemHeight: 10,
      itemWidth: 10,
      left: "left",
      selectedMode: false,
      selectorLabel: {
        fontSize: 12,
      },
      top: "6px",
    },
    chartTitlePosition: "left" as ChartPosition,
    color: ["#D70D09", "#FFA620", "#F8D904", "#1A90FE", "#D5DEE6"],
    containerHeight: "238px",
    pieCenterLabel: t($ => $.customers.title),
    pieCenterText: formatNumber(totalCount, i18next),
    position: "center" as ChartPosition,
  };

  const seriesData: PieDataItem[] = [
    createChurnStatusSeriesItem(ChurnStatus.High),
    createChurnStatusSeriesItem(ChurnStatus.Moderate),
    createChurnStatusSeriesItem(ChurnStatus.Low),
    createChurnStatusSeriesItem(ChurnStatus.Insufficient),
    createChurnStatusSeriesItem(ChurnStatus.NoRisk),
  ];

  function createChurnStatusSeriesItem (status: ChurnStatus): PieDataItem {
    const count = churnStatusCounts?.[status] ?? 0;
    const percent = formatPercent((count / totalCount) * 100, i18next);
    return {
      labelText: `{bold|${percent}}`,
      name: status,
      value: count || '-',
    };
  }

  return (
    <Card
      cardContentStyle={{ padding: 0 }}
      sx={{ padding: "18px 20px 32px 20px" }}
      testID="insight-card"
    >
      <Text fontSize="18px" fontWeight={400} sx={{ lineHeight: "22px" }} >
        {t($ => $.insights.churn.pieChart)}
      </Text>
      <TierProgressPieChart chartOptions={chartOptions} seriesData={seriesData} />
    </Card>
  );
};
